import * as React from "react";
import { CaregiverAvailability, DayAvailability } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { AppListDivider } from "../Generic/AppList";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import DayAvailabilityCard from "./components/DayAvailabilityCard";
import MaxTravelDistanceAvailabilityCard from "./components/MaxTravelDistanceAvailabilityCard";
import VisitDurationAvailabilityCard, {
  MaxDuration,
  MinDuration,
} from "./components/VisitDurationAvailabilityCard";

export interface AvailabilityScreenProps {
  availability: CaregiverAvailability;
  error?: string;
  onChangeAvailability: (availability: CaregiverAvailability) => void;
  onClickGoBack: () => void;
}

const CaseSettingsScreen = (props: AvailabilityScreenProps) => {
  const handleChangeDayTimes = React.useCallback(
    (value: [number, number], changedDay: DayAvailability) => {
      props.onChangeAvailability.call(null, {
        maxDistance: props.availability.maxDistance,
        minDuration: props.availability.minDuration,
        maxDuration: props.availability.maxDuration,
        dayAvailabilities: props.availability.dayAvailabilities.map((dayAvailability) => {
          if (changedDay.day === dayAvailability.day) {
            return {
              day: dayAvailability.day,
              startHour: value[0],
              endHour: value[1],
              busyAllDay: dayAvailability.busyAllDay,
            };
          }
          return dayAvailability;
        }),
      });
    },
    [
      props.availability.dayAvailabilities,
      props.availability.maxDistance,
      props.availability.maxDuration,
      props.availability.minDuration,
      props.onChangeAvailability,
    ]
  );

  const handleChangeBusyAllDay = React.useCallback(
    (busyAllDay: boolean, changedDay: DayAvailability) => {
      props.onChangeAvailability.call(null, {
        maxDistance: props.availability.maxDistance,
        minDuration: props.availability.minDuration,
        maxDuration: props.availability.maxDuration,
        dayAvailabilities: props.availability.dayAvailabilities.map((dayAvailability) => {
          if (changedDay.day === dayAvailability.day) {
            return {
              day: dayAvailability.day,
              startHour: dayAvailability.startHour,
              endHour: dayAvailability.endHour,
              busyAllDay: busyAllDay,
            };
          }

          return dayAvailability;
        }),
      });
    },
    [
      props.availability.dayAvailabilities,
      props.availability.maxDistance,
      props.availability.maxDuration,
      props.availability.minDuration,
      props.onChangeAvailability,
    ]
  );

  const handleChangeDistance = React.useCallback(
    (distance: number) => {
      props.onChangeAvailability.call(null, {
        minDuration: props.availability.minDuration,
        maxDuration: props.availability.maxDuration,
        maxDistance: distance,
        dayAvailabilities: props.availability.dayAvailabilities,
      });
    },
    [
      props.availability.dayAvailabilities,
      props.availability.maxDuration,
      props.availability.minDuration,
      props.onChangeAvailability,
    ]
  );

  const handleChangeDuration = React.useCallback(
    (duration: [MinDuration, MaxDuration]) => {
      props.onChangeAvailability.call(null, {
        minDuration: duration[0],
        maxDuration: duration[1],
        maxDistance: props.availability.maxDistance,
        dayAvailabilities: props.availability.dayAvailabilities,
      });
    },
    [
      props.availability.dayAvailabilities,
      props.availability.maxDistance,
      props.onChangeAvailability,
    ]
  );

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.CASE_SETTINGS)} />}
      className="CaseSettingsScreen"
      onClickGoBack={props.onClickGoBack}
    >
      {props.availability.dayAvailabilities.map((day, i) => (
        <AppSlideFadeIn key={day.day} delay={i * 100 + 250}>
          <DayAvailabilityCard
            day={day}
            onChangeTimes={handleChangeDayTimes}
            onChangeBusyAllDay={handleChangeBusyAllDay}
          />
        </AppSlideFadeIn>
      ))}

      <AppListDivider />

      <MaxTravelDistanceAvailabilityCard
        maxDistance={props.availability.maxDistance}
        onChange={handleChangeDistance}
      />

      <VisitDurationAvailabilityCard
        minDuration={props.availability.minDuration}
        maxDuration={props.availability.maxDuration}
        onChange={handleChangeDuration}
      />
    </Scaffold>
  );
};

export default React.memo(CaseSettingsScreen);
